/**
 * Phase 5D — Admin V5 Pricing Settings.
 *
 * GET/PUT /api/admin/pricing/settings + POST /api/admin/pricing/fx/refresh.
 * All amounts shown in AUD; FX rates apply only at checkout.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, RefreshCcw, Save, DollarSign, Wallet, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const AdminPricingPanel = () => {
  const [snap, setSnap] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSnap = useCallback(async () => {
    try {
      const r = await ax.get('/api/admin/pricing/settings');
      setSnap(r.data);
      setForm({
        session_price_aud: ((r.data?.session_price_aud_cents || 0) / 100).toFixed(2),
        intensive_price_aud: ((r.data?.intensive_price_aud_cents || 0) / 100).toFixed(2),
        intensive_credits: r.data?.intensive_credits ?? 4,
        fx_aud_to_usd: r.data?.fx_aud_to_usd ?? 0.65,
        fx_aud_to_kes: r.data?.fx_aud_to_kes ?? 86.5,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load pricing settings');
    }
  }, []);

  useEffect(() => { fetchSnap(); }, [fetchSnap]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        session_price_aud_cents: Math.round(parseFloat(form.session_price_aud) * 100),
        intensive_price_aud_cents: Math.round(parseFloat(form.intensive_price_aud) * 100),
        intensive_credits: parseInt(form.intensive_credits, 10),
        fx_aud_to_usd: parseFloat(form.fx_aud_to_usd),
        fx_aud_to_kes: parseFloat(form.fx_aud_to_kes),
      };
      const r = await ax.put('/api/admin/pricing/settings', payload);
      setSnap(r.data?.current || null);
      toast.success('Pricing & FX updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const refreshFx = async () => {
    setRefreshing(true);
    try {
      const r = await ax.post('/api/admin/pricing/fx/refresh', {});
      if (r.data?.ok) toast.success(`Live rates pulled — USD ${r.data.fx_aud_to_usd}, KES ${r.data.fx_aud_to_kes}`);
      else toast.info(r.data?.reason || r.data?.error || 'FX refresh skipped');
      await fetchSnap();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'FX refresh failed');
    } finally { setRefreshing(false); }
  };

  if (!snap) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <Card data-testid="admin-pricing-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5" /> Pricing & FX Settings</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            All platform amounts are stored in AUD. FX rates apply only at the checkout boundary.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={refreshFx} disabled={refreshing} data-testid="pricing-fx-refresh">
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Pull live FX
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900 flex items-start gap-2" data-testid="pricing-paystack-banner">
          <span className="font-bold uppercase tracking-wider text-amber-700">Paystack:</span>
          <span>
            Only <strong>USD</strong> is accepted at checkout today. AUD remains the platform's source-of-truth — AUD prices below are converted to USD at the Paystack boundary using the FX rate set here. KES is shown for display only until Paystack enables it on the merchant account.
          </span>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Section icon={<Wallet className="h-4 w-4 text-red-700" />} title="1-on-1 Session Price">
            <Field label="Per 60-min session (AUD)" value={form.session_price_aud}
              onChange={(v) => setForm({ ...form, session_price_aud: v })} testid="input-session-price" suffix="AUD" />
          </Section>
          <Section icon={<Wallet className="h-4 w-4 text-amber-700" />} title="Intensive Package">
            <Field label="Package price (AUD)" value={form.intensive_price_aud}
              onChange={(v) => setForm({ ...form, intensive_price_aud: v })} testid="input-intensive-price" suffix="AUD" />
            <Field label="Session credits granted" value={form.intensive_credits}
              onChange={(v) => setForm({ ...form, intensive_credits: v })} testid="input-intensive-credits" suffix="sessions" />
          </Section>
          <Section icon={<Globe className="h-4 w-4 text-blue-700" />} title="FX rates (1 AUD →)">
            <Field label="USD" value={form.fx_aud_to_usd}
              onChange={(v) => setForm({ ...form, fx_aud_to_usd: v })} testid="input-fx-usd" suffix="USD" step="0.0001" />
            <Field label="KES" value={form.fx_aud_to_kes}
              onChange={(v) => setForm({ ...form, fx_aud_to_kes: v })} testid="input-fx-kes" suffix="KES" step="0.01" />
            {snap.fx_last_refreshed_at && (
              <div className="text-[10px] text-gray-400 mt-1">
                Last refreshed: {new Date(snap.fx_last_refreshed_at).toLocaleString()} · source: {snap.fx_source || 'manual'}
              </div>
            )}
          </Section>
        </div>

        <div className="rounded-xl border border-gray-200 p-4 bg-gray-50">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Live preview at AUD 55</div>
          <div className="grid sm:grid-cols-3 gap-3 text-xs">
            <Preview cur="AUD" amt={55} testid="preview-aud" />
            <Preview cur="USD" amt={55 * (parseFloat(form.fx_aud_to_usd) || 0)} testid="preview-usd" />
            <Preview cur="KES" amt={55 * (parseFloat(form.fx_aud_to_kes) || 0)} testid="preview-kes" />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={fetchSnap} data-testid="pricing-reload"><RefreshCcw className="h-4 w-4 mr-1" /> Reload</Button>
          <Button onClick={save} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="pricing-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Pricing
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Section = ({ icon, title, children }) => (
  <div className="rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-2 mb-3">{icon}<div className="text-[11px] uppercase tracking-wider text-gray-700 font-bold">{title}</div></div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, testid, suffix, step = '0.01' }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">{label}</span>
    <div className="flex items-center gap-2">
      <Input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm" data-testid={testid} />
      {suffix && <span className="text-[11px] text-gray-500">{suffix}</span>}
    </div>
  </label>
);

const Preview = ({ cur, amt, testid }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-3 text-center" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{cur}</div>
    <div className="text-base font-bold text-gray-900">{cur} {amt.toFixed(2)}</div>
  </div>
);

export default AdminPricingPanel;
